import React, { useMemo, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useFirestoreConnect, isLoaded, isEmpty } from '../../compat/reactReduxFirebase';
import { connect } from '../../compat/reactRedux';
import { firestoreConnect } from '../../compat/reactReduxFirebase';
import { compose } from '../../compat/redux';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { Box, Button, TableContainer, Paper, Table, TableRow, TableBody, TableCell, TableHead } from '@mui/material';
import HorizontalAds from '../../layout/Ads/HorizontalAds';
import Loader from '../../layout/Loader';
import Page404 from '../Page404';
import parse from 'html-react-parser';
import SEO from '../../components/SEO';
import { READINGS_COLLECTION, getReadingTimestamp, getReadingValue, timestampToDate, timestampToMillis } from '../../utils/readings';

import { makeStyles } from '../../compat/muiStyles';
import mainStyle from '../../assets/mainStyle';

const useStyles = makeStyles(() => ({
  ...mainStyle,
  root: {
    padding: '30px',
    width: '100%',
  },
  title: {
    color: '#444',
    textDecoration: 'none',
  },
}));

const PUBLIC_READING_FIELDS = ['timestamp', 'temperatureC', 'batteryVoltage', 'rssiDbm'];

TimeAgo.addLocale(en);
const timeAgo = new TimeAgo('en-US');

const ActivityDevice = ({ Details, Readings, user, Loaded, Empty }) => {
  const classes = useStyles();
  const { id } = useParams();
  const [limit, setLimit] = useState(30);

  useFirestoreConnect([
    { collection: 'Devices', storeAs: 'ActivityDetails', doc: id },
    {
      collection: 'Devices',
      storeAs: 'ActivityReadings',
      doc: id,
      subcollections: [{ collection: READINGS_COLLECTION, limit, orderBy: ['timestamp', 'desc'] }],
    },
  ]);

  const inputs = useMemo(() => {
    if (Details && Details.inputs) {
      return Object.keys(Details.inputs);
    }
    return PUBLIC_READING_FIELDS.filter((field) => field !== 'timestamp');
  }, [Details]);

  if (!Loaded) return <Loader />;
  if (Empty) return <Page404 />;

  const created = timestampToDate(Details.created);
  const lastTimestamp = timestampToDate(Details.lastTimestamp || Details.lasttimeStamp);
  const description = Details.description
    ? `${Details.name || 'Climate Sens device'} public climate and sensor readings.`
    : 'Climate Sens public climate and sensor readings.';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: Details.name ? `${Details.name} readings` : `Climate Sens readings ${id}`,
    description,
    url: `https://climatesens.com/activity/${id}`,
    creator: {
      '@type': 'Organisation',
      name: 'Climate Sens',
      url: 'https://climatesens.com',
    },
    variableMeasured: inputs,
  };

  return (
    <main className={classes.root}>
      <SEO
        title={Details.name ? `${Details.name} readings` : 'Climate readings'}
        description={description}
        path={`/activity/${id}`}
        type="article"
        structuredData={structuredData}
      />

      <Box>
        <h1>Readings - {Details.name}</h1>
        {Details.admin && user && Details.admin.includes(user.uid) ? (
          <Button sx={{ marginBottom: '10px' }} component={NavLink} to={`/activity/${id}/edit`} variant="outlined">Edit</Button>
        ) : null}
        {created ? <Box><strong>Date created:</strong> {created.toUTCString()} ({timeAgo.format(created.getTime(), 'time')})</Box> : null}
        {lastTimestamp ? <Box><strong>Last message:</strong> {lastTimestamp.toUTCString()} ({timeAgo.format(lastTimestamp.getTime(), 'time')})</Box> : null}
        {Details.type ? <Box><strong>Device: </strong><NavLink to={`/device/${Details.type}`}>{Details.type}</NavLink></Box> : null}
        {Details.location ? <Box><strong>Location: </strong>{Details.location}</Box> : null}
        {Details.private ? <Box><strong>Private:</strong> True</Box> : null}
        {Details.description ? <Box>{parse(Details.description)}</Box> : null}

        {inputs.length ? (
          <Box>
            <h2>Public reading fields</h2>
            <ul>
              {inputs.map((input) => <li key={input}>{input}</li>)}
            </ul>
          </Box>
        ) : null}
      </Box>

      <HorizontalAds />

      <Box>
        <h1>Readings</h1>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="public device readings">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                {inputs.map((input) => <TableCell key={input} align="right">{input}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {Readings && Readings.map((reading) => {
                const timestamp = getReadingTimestamp(reading);
                const timestampDate = timestampToDate(timestamp);
                const timestampMs = timestampToMillis(timestamp);
                const label = timestampDate ? timestampDate.toUTCString() : reading.id;
                const ago = timestampMs ? timeAgo.format(timestampMs, 'time') : '';

                return (
                  <TableRow key={reading.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">{label}{ago ? ` (${ago})` : ''}</TableCell>
                    {inputs.map((input) => {
                      const value = getReadingValue(reading, input);
                      return <TableCell key={`${reading.id}-${input}`} align="right">{value ?? ''}</TableCell>;
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoaded(Readings) ? <Loader /> : null}
        {isEmpty(Readings) ? <Box className={classes.NoActiveDevices}>No public readings.</Box> : null}

        <Box className={classes.LoadMoreBox}>
          <Button className={classes.LoadMoreButton} variant="outlined" onClick={() => setLimit(limit + 50)}>Load More</Button>
        </Box>
      </Box>

      <HorizontalAds />
    </main>
  );
};

const mapStateToProps = (state) => {
  let ActivityDetails = null;
  if (state.firestore.ordered.ActivityDetails) {
    ActivityDetails = state.firestore.ordered.ActivityDetails[0];
  }

  return {
    user: firebase.auth().currentUser,
    Details: ActivityDetails,
    Readings: state.firestore.ordered.ActivityReadings,
    Loaded: isLoaded(state.firestore.ordered.ActivityDetails),
    Empty: isEmpty(state.firestore.ordered.ActivityDetails),
  };
};

export default compose(
  connect(mapStateToProps),
  firestoreConnect(() => [])
)(ActivityDevice);
